import { hasActiveSeat, type SeatLite } from "@/lib/accountLiveness";

export type IdleAccount = {
  id: string;
  name: string;
  photo: string | null;
  workspace: string;
  workspace_id: string | null;
  paying: boolean;
  renewal: string | null;
  pausedCount: number;
  lastPauseReason: string | null;
  lastActivity: string | null;
  totalCampaigns: number;
};

const RUNNING = ["active", "resuming"];

/** Connected, non-demo, non-master accounts with no running campaign. RLS scopes sub admins. */
export async function fetchIdleAccounts(supabase: any): Promise<IdleAccount[]> {
  const { data: clients } = await supabase
    .from("clients")
    .select("id, linkedin_name, profile_picture_url, workspace_id, is_master_sales_nav, workspaces(name)")
    .eq("status", "active")
    .eq("is_demo", false)
    .limit(5000);
  const list = ((clients || []) as any[]).filter((c) => !c.is_master_sales_nav);
  const ids = list.map((c) => c.id);
  if (!ids.length) return [];
  const [{ data: camps }, { data: seats }] = await Promise.all([
    supabase.from("campaigns").select("client_id, status, paused_reason, updated_at").in("client_id", ids).limit(20000),
    supabase.from("account_subscriptions").select("client_id, status, current_period_end").in("client_id", ids).limit(20000),
  ]);
  const campBy = new Map<string, any[]>();
  (camps || []).forEach((c: any) => campBy.set(c.client_id, [...(campBy.get(c.client_id) || []), c]));
  const seatBy = new Map<string, SeatLite[]>();
  (seats || []).forEach((s: SeatLite) => s.client_id && seatBy.set(s.client_id, [...(seatBy.get(s.client_id) || []), s]));

  const out: IdleAccount[] = [];
  for (const c of list) {
    const cs = campBy.get(c.id) || [];
    if (cs.some((x) => RUNNING.includes(x.status))) continue;
    const ss = seatBy.get(c.id) || [];
    const paused = cs.filter((x) => x.status === "paused").sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    const last = cs.reduce<string | null>((m, x) => (!m || (x.updated_at || "") > m ? x.updated_at : m), null);
    const ends = ss.map((s) => s.current_period_end).filter(Boolean).sort() as string[];
    out.push({
      id: c.id,
      name: c.linkedin_name || "Unnamed account",
      photo: c.profile_picture_url,
      workspace: c.workspaces?.name || "—",
      workspace_id: c.workspace_id,
      paying: hasActiveSeat(ss),
      renewal: ends[ends.length - 1] || null,
      pausedCount: paused.length,
      lastPauseReason: paused[0]?.paused_reason ?? null,
      lastActivity: last,
      totalCampaigns: cs.length,
    });
  }
  return out;
}
